import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GlobalActivityPill } from './GlobalActivityPill.js';
import { IconButton } from './ui/IconButton.js';
import { EvoLogo } from './common/EvoLogo.js';
import { Icon } from './Icon.js';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import { useSession } from '../contexts/SessionContext.js';
import { useTheme } from '../hooks/useTheme.js';
import i18n from '../i18n.js';

const SIDEBAR_W = 200;
const SIDEBAR_W_COLLAPSED = 80;

function greetingKey(): 'morning' | 'afternoon' | 'evening' {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 19) return 'afternoon';
  return 'evening';
}

interface TopBarProps {
  collapsed: boolean;
}

/* Header V2 (handoff): UNA banda de 76px — hero 36 · título 35px · acciones
 * de página · grupo fijo derecho (tema / actividad / fecha). La fila de
 * fecha separada del header viejo de 152px ya no existe. */
export function TopBar({ collapsed }: TopBarProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { override } = useTopBarCtx();
  const { displayName } = useSession();
  const { theme, toggleTheme } = useTheme();
  const sidebarW = collapsed ? SIDEBAR_W_COLLAPSED : SIDEBAR_W;

  const dateStr = new Date().toLocaleDateString(i18n.language, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  let defaultTitle: string | null = null;
  if (pathname === '/') {
    defaultTitle = t(`home.greeting.${greetingKey()}`, { name: displayName ?? '' });
  }

  const title = override.title ?? defaultTitle;
  const subtitle = override.subtitle;
  const heroIcon = override.heroIcon;
  const heroIconColor = override.heroIconColor ?? 'var(--evo-cyan)';
  const bottomActions = override.bottomActions;

  return (
    <header
      style={{
        display: 'grid',
        gridTemplateColumns: `${sidebarW}px 1fr`,
        height: '76px',
        borderBottom: '1px solid var(--evo-stroke)',
        flexShrink: 0,
        transition: 'grid-template-columns 180ms ease',
      }}
    >
      {/* Celda del logo — espeja el ancho del sidebar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: collapsed ? '0 8px' : '16px',
          borderRight: '1px solid var(--evo-stroke)',
          transition: 'padding 180ms ease',
        }}
      >
        <EvoLogo size="md" iconOnly={collapsed} />
      </div>

      {/* Banda del título */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '0 var(--evo-content-pad-x) 0 var(--evo-page-pad-x)',
          gap: 'var(--evo-space-4)',
          minWidth: 0,
        }}
      >
        {heroIcon && (
          <span
            style={{
              flexShrink: 0,
              lineHeight: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: heroIconColor,
            }}
          >
            <Icon name={heroIcon} size={36} strokeWidth={1.5} />
          </span>
        )}

        {title && (
          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <h1
              style={{
                margin: 0,
                fontFamily: 'var(--evo-font)',
                fontSize: 'var(--evo-fs-page-title)',
                lineHeight: 'var(--evo-lh-page-title)',
                color: 'var(--evo-fg-1)',
                fontWeight: 400,
                letterSpacing: '-0.01em',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {title}
            </h1>
            {subtitle && (
              <p style={{
                margin: '2px 0 0',
                fontSize: 'var(--evo-fs-body)',
                color: 'var(--evo-fg-3)',
                fontFamily: 'var(--evo-font)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {subtitle}
              </p>
            )}
          </div>
        )}

        {bottomActions && (
          <div style={{ marginLeft: title ? undefined : 'auto', flexShrink: 0 }}>
            {bottomActions}
          </div>
        )}

        {/* Grupo fijo derecho: tema · actividad · fecha */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--evo-space-3)',
            flexShrink: 0,
            marginLeft: bottomActions || title ? undefined : 'auto',
          }}
        >
          <IconButton
            variant="outline"
            tone="muted"
            size="sm"
            ariaLabel={theme === 'light' ? t('theme.toDark') : t('theme.toLight')}
            tooltip={theme === 'light' ? t('theme.toDark') : t('theme.toLight')}
            icon={<Icon name={theme === 'light' ? 'moon' : 'sun'} size={14} />}
            onClick={toggleTheme}
          />
          <GlobalActivityPill />
          <span
            style={{
              fontSize: 'var(--evo-fs-caption)',
              lineHeight: 1.5,
              color: 'var(--evo-fg-3)',
              textTransform: 'capitalize',
              whiteSpace: 'nowrap',
            }}
          >
            {dateStr}
          </span>
        </div>
      </div>
    </header>
  );
}
